import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Plus, Trash2, Edit, School, Shield, Zap, Crown, Settings, AlertTriangle, Search } from "lucide-react";
import { handleGet, handleRemove } from "../../controllers/controller.all";
import { getImageUrl } from "../../services/service.api";
import Pagination from "../../components/ui/Pagination";
import SortableHeader from "../../components/ui/SortableHeader";

const PLAN_CONFIG = {
  STANDARD: { label: "Standard", icon: Shield, color: "text-indigo-600 bg-indigo-50 ring-indigo-200" },
  PREMIUM:  { label: "Premium",  icon: Zap,    color: "text-violet-600 bg-violet-50 ring-violet-200" },
  VIP:      { label: "VIP",      icon: Crown,   color: "text-amber-600 bg-amber-50 ring-amber-200" },
};

function PlanBadge({ plan }) {
  const cfg = PLAN_CONFIG[plan] || PLAN_CONFIG.STANDARD;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ${cfg.color}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

function QuotaAlert({ ecole }) {
  // Show warning if any resource >= 80% consumed
  const resources = ["sms", "mail", "ai_student", "ai_teacher"];
  const hasAlert = resources.some(r => {
    const base = ecole[`quota_${r}`];
    const consumed = ecole[`consumed_${r}`] || 0;
    if (base === -1 || !base) return false;
    const total = base + (ecole[`extra_${r}`] || 0);
    return total > 0 && (consumed / total) >= 0.8;
  });
  if (!hasAlert) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 ring-1 ring-red-200" title="Quota presque épuisé">
      <AlertTriangle className="h-3 w-3" /> Quota
    </span>
  );
}

export default function EcoleGlobalList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    
    const [ecoles, setEcoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    // Pagination & Sorting state
    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey, setSortKey] = useState("nom");
    const [sortOrder, setSortOrder] = useState("asc");
    const itemsPerPage = 20;

    const fetchEcoles = async () => {
        setLoading(true);
        await handleGet(`/ecole?skip=0&limit=100000`, (res) => {
            if (res?.ecoles) {
                setEcoles(res.ecoles);
            }
        }, token);
        setLoading(false);
    };

    useEffect(() => {
        if(token) fetchEcoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleDelete = async (id) => {
        await handleRemove(`/ecole/${id}`, fetchEcoles, token, {
            message: "Êtes-vous sûr de vouloir supprimer cette école ?"
        });
    };

    const handleSort = (key, order) => {
        setSortKey(key);
        setSortOrder(order);
    };

    const filteredEcoles = ecoles.filter(ecole => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (ecole.nom?.toLowerCase().includes(s) || 
                ecole.sigle?.toLowerCase().includes(s) || 
                ecole.ville?.toLowerCase().includes(s) || 
                ecole.pays?.toLowerCase().includes(s));
    });

    const sortedEcoles = [...filteredEcoles].sort((a, b) => {
        let valA = a[sortKey] || "";
        let valB = b[sortKey] || "";
        
        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    const paginatedEcoles = sortedEcoles.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Gestion des Écoles</h1>
                    <p className="text-sm text-gray-500 mt-1">Liste de tous les établissements enregistrés.</p>
                </div>
                <Link 
                    to="/super-admin/ecoles/create" 
                    className="inline-flex items-center justify-center gap-2 bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition w-full sm:w-auto"
                >
                    <Plus className="h-4 w-4" /> Nouvelle École
                </Link>
            </div>

            {/* Search bar */}
            <div className="relative w-full text-gray-400">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" />
                <input 
                    type="text" 
                    placeholder="Rechercher (nom, ville, pays)..." 
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                />
            </div>

            {/* Mobile cards */}
            <div className="flex flex-col gap-3 md:hidden">
                {loading ? (
                    <div className="text-center py-10 text-sm text-gray-500">Chargement...</div>
                ) : paginatedEcoles.length === 0 ? (
                    <div className="text-center py-10 text-sm text-gray-500">Aucune école trouvée.</div>
                ) : (
                    paginatedEcoles.map((ecole) => (
                        <div key={ecole._id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                                    {ecole.logo ? (
                                        <img src={getImageUrl(ecole.logo)} alt={ecole.nom} className="h-10 w-10 rounded-lg object-cover" />
                                    ) : (
                                        <School className="h-5 w-5" />
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-900 truncate">{ecole.nom}</div>
                                    <div className="text-xs text-gray-500">{ecole.sigle} · {ecole.ville}, {ecole.pays}</div>
                                    <div className="flex items-center gap-1.5 mt-1">
                                      <PlanBadge plan={ecole.plan || "STANDARD"} />
                                      <QuotaAlert ecole={ecole} />
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <Link 
                                    to="/super-admin/ecoles/plan"
                                    state={{ id: ecole._id }}
                                    className="text-violet-600 bg-violet-50 p-2 rounded-lg transition hover:bg-violet-100"
                                    title="Gérer le plan"
                                >
                                    <Settings className="h-4 w-4" />
                                </Link>
                                <Link 
                                    to={`/super-admin/ecoles/edit/${ecole._id}`}
                                    className="text-indigo-600 bg-indigo-50 p-2 rounded-lg transition hover:bg-indigo-100"
                                    title="Modifier"
                                >
                                    <Edit className="h-4 w-4" />
                                </Link>
                                <button 
                                    onClick={() => handleDelete(ecole._id)}
                                    className="text-red-500 bg-red-50 p-2 rounded-lg transition hover:bg-red-100"
                                    title="Supprimer"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <SortableHeader label="Nom de l'École" sortKey="nom" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Plan" sortKey="plan" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Téléphone" sortKey="numero" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Ville/Pays" sortKey="ville" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Chargement...</td>
                                </tr>
                            ) : paginatedEcoles.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Aucune école trouvée.</td>
                                </tr>
                            ) : (
                                paginatedEcoles.map((ecole) => (
                                    <tr key={ecole._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                                                    {ecole.logo ? (
                                                        <img src={getImageUrl(ecole.logo)} alt={ecole.nom} className="h-10 w-10 rounded-lg object-cover" />
                                                    ) : (
                                                        <School className="h-5 w-5" />
                                                    )}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{ecole.nom}</div>
                                                    <div className="text-xs text-gray-500">{ecole.sigle}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                <PlanBadge plan={ecole.plan || "STANDARD"} />
                                                <QuotaAlert ecole={ecole} />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-900">{ecole.numero}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{ecole.ville}</div>
                                            <div className="text-xs text-gray-500">{ecole.pays}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                <Link 
                                                    to="/super-admin/ecoles/plan"
                                                    state={{ id: ecole._id }}
                                                    className="text-violet-600 hover:text-violet-900 bg-violet-50 p-2 rounded-lg transition"
                                                    title="Gérer le plan"
                                                >
                                                    <Settings className="h-4 w-4" />
                                                </Link>
                                                <Link 
                                                    to={`/super-admin/ecoles/edit/${ecole._id}`}
                                                    className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 p-2 rounded-lg transition"
                                                    title="Modifier"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Link>
                                                <button 
                                                    onClick={() => handleDelete(ecole._id)}
                                                    className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-lg transition"
                                                    title="Supprimer"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Pagination Controls */}
            {filteredEcoles.length > itemsPerPage && (
                <Pagination 
                    currentPage={currentPage} 
                    totalItems={filteredEcoles.length} 
                    itemsPerPage={itemsPerPage} 
                    onPageChange={setCurrentPage} 
                />
            )}
        </div>
    );

}
